import React, { useCallback, useEffect, useState } from "react";
import { Add, DeleteOutline, Pause, PlayArrow } from "@material-ui/icons";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { Avatar, Button, Chip, CircularProgress, IconButton, Paper, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography } from "@material-ui/core";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { formatMontevideoDateTime } from "../../utils/dateTime";

const blank = { contactId: null, contactName: "", message: "", scheduledAt: "", status: "borrador" };

const initials = (name = "") => name.split(/\s+/).filter(Boolean).slice(0, 2).map(part => part[0]).join("").toUpperCase() || "C";

export default function ScheduledMessages() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(blank);
  const [contacts, setContacts] = useState([]);
  const [contactsLoading, setContactsLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/crm/scheduled-messages/");
      setItems(data.messages || []);
    } catch (error) {
      toastError(error);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    let active = true;
    const loadContacts = async () => {
      try {
        const { data } = await api.get("/crm/contact-options?search=");
        if (active) setContacts(data.contacts || []);
      } catch (error) {
        if (active) setContacts([]);
      } finally {
        if (active) setContactsLoading(false);
      }
    };
    loadContacts();
    return () => { active = false; };
  }, []);

  const save = async event => {
    event.preventDefault();
    try {
      await api.post("/crm/scheduled-messages", { ...form, scheduledAt: new Date(form.scheduledAt).toISOString() });
      setForm(blank);
      await load();
    } catch (error) {
      toastError(error);
    }
  };

  const changeStatus = async (item, status) => {
    try {
      await api.put(`/crm/scheduled-messages/${item.id}`, { ...item, status });
      await load();
    } catch (error) {
      toastError(error);
    }
  };

  const remove = async id => {
    try {
      await api.delete(`/crm/scheduled-messages/${id}`);
      await load();
    } catch (error) {
      toastError(error);
    }
  };

  return (
    <MainContainer>
      <MainHeader><Title>Mensajes programados</Title><Chip label={"Env\u00edos externos pausados"} /></MainHeader>
      <div style={{ padding: 16, overflowY: "auto", minWidth: 0 }}>
        <Typography color="textSecondary" paragraph>
          {"Prepar\u00e1 seguimientos y recordatorios. Se guardan como borrador o programados, pero no se enviar\u00e1n autom\u00e1ticamente."}
        </Typography>
        <Paper variant="outlined" style={{ padding: 16, marginBottom: 16 }} component="form" onSubmit={save}>
          <Typography variant="subtitle1">Nuevo mensaje programado</Typography>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))", gap: 12, marginTop: 12 }}>
            <Autocomplete
              fullWidth
              options={contacts}
              loading={contactsLoading}
              value={contacts.find(contact => String(contact.id) === String(form.contactId)) || null}
              inputValue={form.contactName}
              getOptionLabel={option => option?.name || ""}
              noOptionsText="No se encontraron clientes"
              loadingText="Cargando clientes..."
              onChange={(_event, contact) => setForm({
                ...form,
                contactId: contact?.id || null,
                contactName: contact?.name || "",
              })}
              onInputChange={(_event, value, reason) => {
                if (reason === "input" || reason === "clear") {
                  setForm(current => ({ ...current, contactName: value, contactId: null }));
                }
              }}
              renderOption={contact => (
                <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0, padding: "4px 0" }}>
                  <Avatar src={contact.profilePicUrl} style={{ width: 34, height: 34, fontSize: 13 }}>{initials(contact.name)}</Avatar>
                  <div style={{ minWidth: 0 }}>
                    <Typography noWrap variant="body2" style={{ fontWeight: 700 }}>{contact.name}</Typography>
                    <Typography noWrap variant="caption" color="textSecondary">{contact.number || "Sin teléfono registrado"}</Typography>
                  </div>
                </div>
              )}
              renderInput={params => (
                <TextField
                  {...params}
                  required
                  label="Cliente"
                  placeholder="Buscar por nombre o teléfono"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: <>{contactsLoading ? <CircularProgress color="inherit" size={18} /> : null}{params.InputProps.endAdornment}</>,
                  }}
                />
              )}
            />
            <TextField fullWidth label="Fecha y hora" required type="datetime-local" InputLabelProps={{ shrink: true }} value={form.scheduledAt} onChange={event => setForm({ ...form, scheduledAt: event.target.value })} />
            <TextField fullWidth label="Mensaje" required multiline rowsMin={2} value={form.message} onChange={event => setForm({ ...form, message: event.target.value })} />
          </div>
          <Button type="submit" startIcon={<Add />} color="primary" variant="contained" style={{ marginTop: 14 }}>{"Guardar programaci\u00f3n"}</Button>
        </Paper>
        <Paper variant="outlined" style={{ overflowX: "auto" }}>
          <Table size="small">
            <TableHead><TableRow><TableCell>Cliente</TableCell><TableCell>Mensaje</TableCell><TableCell>Programado para</TableCell><TableCell>Estado</TableCell><TableCell align="right">Acciones</TableCell></TableRow></TableHead>
            <TableBody>
              {items.map(item => (
                <TableRow key={item.id}>
                  <TableCell style={{ overflowWrap: "anywhere" }}>{item.contactName}</TableCell>
                  <TableCell style={{ minWidth: 240, whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>{item.message}</TableCell>
                  <TableCell>{formatMontevideoDateTime(item.scheduledAt)}</TableCell>
                  <TableCell><Chip size="small" label={item.status} /></TableCell>
                  <TableCell align="right">
                    <IconButton aria-label="Pausar" onClick={() => changeStatus(item, item.status === "pausado" ? "programado" : "pausado")}>{item.status === "pausado" ? <PlayArrow /> : <Pause />}</IconButton>
                    <IconButton aria-label="Eliminar" onClick={() => remove(item.id)}><DeleteOutline /></IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      </div>
    </MainContainer>
  );
}
